import { computed, ref } from 'vue'

import { conversionApi, type ConversionApi } from '../api/ConversionApi'
import { SaveHardalEventsFlow } from '../flows/SaveHardalEventsFlow'

import { useWorkspace } from '@/features/account'
import { useWorkspaceAttribute } from '@/features/workspace-settings'

export function useHardalEvents(
  api: ConversionApi = conversionApi,
  flow = new SaveHardalEventsFlow(),
) {
  const workspace = useWorkspace()
  const events = useWorkspaceAttribute('hardal_events')
  const primary = useWorkspaceAttribute('hardal_primary_event')
  const catalog = ref<string[]>([])
  const loading = ref(false)
  const pending = ref(false)
  const error = ref<unknown>(null)

  async function load(): Promise<boolean> {
    const workspaceId = workspace.workspaceId.value
    if (workspaceId === undefined) return false
    loading.value = true
    error.value = null
    try {
      catalog.value = await api.getHardalEventNames(workspaceId)
      return true
    } catch (cause) {
      error.value = cause
      catalog.value = []
      return false
    } finally {
      loading.value = false
    }
  }

  async function save(nextEvents: readonly string[], nextPrimary: string | null): Promise<boolean> {
    pending.value = true
    error.value = null
    try {
      await flow.save(nextEvents, nextPrimary, {
        events: events.id.value,
        primary: primary.id.value,
      })
      return true
    } catch (cause) {
      error.value = cause
      return false
    } finally {
      pending.value = false
    }
  }

  return {
    events,
    primary,
    catalog,
    loading,
    pending,
    error,
    hasSaved: computed(() => (events.value.value?.length ?? 0) > 0),
    load,
    save,
  }
}

export type UseHardalEvents = ReturnType<typeof useHardalEvents>
